import {
  type ComponentType,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useSearchParams } from 'next/navigation'

import {
  SearchForm as DefaultSearchForm,
  SearchFormProps,
} from '@awaze/design-system.compositions.search-form'
import { SearchForm as DriveTimeSearchForm } from '@awaze/design-system.compositions.search-form.drivetime'
import {
  GoogleSearchMethods,
  useFetchDestination,
  useFetchDestinationForLegacyList,
  useResolveGooglePlacesID,
} from '@awaze/mfe-common.api.places-api'
import { Container } from '@awazevr/design-system.components.container'
import { SearchControls } from '@awazevr/design-system.compositions.search-controls'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import {
  SORT_ORDER_UPDATED,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awazevr/mfe-common.contexts.search'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'

import {
  MobileStickableContent,
  MobileSticky,
} from '@/components/MobileSticky/MobileSticky'
import { StickableHeaderExtended } from '@/components/MobileSticky/StickableHeaderExtended'
import {
  resolvePlacesId,
  resolveRadiusCentre,
} from '@/components/SearchForm/SearchForm.helper'
import { SearchFormSubmitData } from '@/components/SearchForm/SearchForm.types'
import {
  GD_482_RADIUS_KM,
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
} from '@/constants'
import { useAppBrandConfig, useAppConfig } from '@/context/app-config-provider'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { useApplicationConfig } from '@/lib/applicationConfig'
import { addBoatsToDestinationList } from '@/lib/contentful/boats-option-data'
import { useDisplayContext } from '@/lib/display/displayContext'
import { Assert } from '@/utils/assert'
import { setDisplayModeWrapper } from '@/utils/displayModeOnClickHelper'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'
import {
  getSortOptionsFromConfig,
  shouldDisplaySortOptions,
} from '@/utils/sortOptions/sortOptions'

import { usePlacesSuggestions } from './hooks/usePlacesSuggestions'
import { useSearchSelectionEvent } from './hooks/useSearchSelectionEvent'
import {
  combineAutoSuggestSearch,
  type TmsUpdateDataLayer,
} from './combineAutoSuggestSearch'
import { getAutoSuggestSearch } from './getAutoSuggestSearch'
import {
  getHardcodedDestinationData,
  handleSearchFormSubmit,
  handleSearchFormSummaryClick,
  useGoogleFallback,
} from './SearchForm.utils'
import { handleNearbyPropertiesClick } from './SearchForm.utils.drivetime'

type SearchFormComponentProps = {
  defaultConfig: SearchFormProps['defaultConfig']
  translations: SearchFormProps['translations']
  initialValues: SearchFormProps['initialValues'] & {
    destinationId?: number
  }
  locale: SearchFormProps['locale']
  openFilters: () => void
  marketCode: string
  mobileStickyMode?: boolean
  placesConfig?: {
    useLegacyDestinationSelector: boolean
    initialParentDestinationList: string | undefined
    pageSize: number | undefined
    pageSizePopular: number | undefined
    googleFallbackMethod: GoogleSearchMethods
  }
  isGuestApp?: boolean
}

type SearchFormWithNearbyProps = SearchFormProps & {
  onNearbyPropertiesClick?: () => Promise<unknown>
  onNearbyPropertiesSeen?: () => void
}

const placesConfigLiteral: SearchFormComponentProps['placesConfig'] = {
  useLegacyDestinationSelector: false,
  initialParentDestinationList: undefined,
  pageSize: 20,
  pageSizePopular: 20,
  googleFallbackMethod: undefined,
}

export const SearchFormComponent: React.FC<SearchFormComponentProps> = ({
  defaultConfig,
  initialValues,
  openFilters,
  translations,
  locale,
  marketCode,
  mobileStickyMode = true,
  placesConfig = placesConfigLiteral,
  isGuestApp,
}: SearchFormComponentProps) => {
  const { formatMessage } = useIntlContext()
  const { brand } = useApplicationConfig()
  const tms = useContext(TmsContext)
  const [searchFormClicked, setSearchFormClicked] = useState(false)

  const { searchCriteria, totalSelectedFilters, hasResults } =
    useSearchContextState()

  const {
    handleDestinationSelection,
    handleDateSelection,
    handleGuestSelection,
    handleSearchSelectionStart,
    handleSearchSelectionClose,
  } = useSearchSelectionEvent()

  const appConfig = useAppConfig()

  Assert.defined(searchCriteria, 'searchCriteria is not defined')
  Assert.defined(defaultConfig, 'defaultConfig is undefined')
  Assert.defined(translations, 'translations is undefined')
  Assert.defined(
    translations.destination,
    'translations.destination is undefined',
  )

  const { accommodationType, sort, regionName, ne, sw, placesId } =
    searchCriteria

  const dispatch = useSearchContextDispatch()
  const optimizelyDecisionsContext = useOptimizelyDecisionsContext()

  const appBrandConfig = useAppBrandConfig(brand)

  placesConfig.useLegacyDestinationSelector =
    appBrandConfig.useLegacyDestinationSelector

  const googleSearchMethod = useGoogleFallback(brand)

  if (googleSearchMethod) {
    placesConfig.googleFallbackMethod = googleSearchMethod
  }

  const {
    useLegacyDestinationSelector,
    initialParentDestinationList,
    pageSize,
    pageSizePopular,
    googleFallbackMethod,
  } = placesConfig

  // Places-API Google Coordinates (radiusCentre) fetch
  const [googleID, setGoogleID] = useState<undefined | string>('')
  const radiusCentre = useResolveGooglePlacesID(googleID)

  // Optimizely XP: ACQ-1258 Use Places-API Legacy-List data
  const [fetchPlacesLegacyList, setFetchPlacesLegacyList] = useState(false)

  const placesLegacyListData = useFetchDestinationForLegacyList(
    fetchPlacesLegacyList,
  )

  // drivetime: Drive Time Search
  const driveTimeSearchEnabled = Boolean(
    optimizelyDecisionsContext?.isExperimentVariationKeyOneOf(
      OPTIMIZELY_FLAGS_ENUM.DRIVE_TIME_SEARCH,
      ['variant_a'],
    ),
  )

  const hasTrackedNearbySeen = useRef(false)

  useEffect(() => {
    setFetchPlacesLegacyList(searchFormClicked && useLegacyDestinationSelector)
  }, [searchFormClicked])

  if (brand === 'hoseasons') {
    addBoatsToDestinationList(defaultConfig)
  }

  const getSuggestions = usePlacesSuggestions(googleFallbackMethod)
  const originalAutoSuggestSearch = getAutoSuggestSearch(
    brand,
    marketCode,
    appConfig,
  )

  const getDestination = useFetchDestination()
  const searchParams = useSearchParams()

  const searchFormTranslations: SearchFormProps['translations'] = {
    ...translations,
    destination: {
      ...translations.destination,
      ...(driveTimeSearchEnabled
        ? {
            nearby: {
              ctaTitle: formatMessage({
                id: 'nearby.ctaTitle',
                defaultMessage: 'Search nearby',
              }),
              ctaDescription: formatMessage({
                id: 'nearby.ctaDescription',
                defaultMessage: 'Discover properties around you',
              }),
              destinationField: formatMessage({
                id: 'nearby.destinationField',
                defaultMessage: 'Search nearby',
              }),
              geolocationPermissionWarning: formatMessage({
                id: 'nearby.geolocationPermissionWarning',
                defaultMessage:
                  'You must allow your browser location request to view nearby properties.',
              }),
            },
          }
        : {}),
    },
  }
  const SearchFormForVariant = (
    driveTimeSearchEnabled ? DriveTimeSearchForm : DefaultSearchForm
  ) as ComponentType<SearchFormWithNearbyProps>
  const handleNearbyPropertiesSeen = () => {
    if (hasTrackedNearbySeen.current) {
      return
    }

    hasTrackedNearbySeen.current = true
    trackClientSideOptimizelyEvent(
      OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_SEEN,
      optimizelyDecisionsContext,
    )
  }

  const searchForm = (stuck: boolean) => (
    <div
      onClick={() => {
        if (!searchFormClicked) {
          setSearchFormClicked(() => true)
        }
      }}
    >
      <SearchFormForVariant
        defaultConfig={{
          ...defaultConfig,
          autocompleteDebounceWait: 600,
          destinationListConfig: {
            ...(initialParentDestinationList && {
              initialParent: initialParentDestinationList,
            }),
            destinationFetch: async (locale, id, accommodationType) => {
              const idStringified = id?.toString()
              const destinationId =
                idStringified === 'countries' ? undefined : idStringified
              const destinations =
                accommodationType !== 'boat-holidays'
                  ? getDestination(destinationId, true)
                  : getHardcodedDestinationData(locale, idStringified)

              return await destinations
            },
            pageSize,
            pageSizePopular,
            fetchDelay: 0,
            sortAlphabetically: true,
          },
        }}
        autoSuggestSearch={combineAutoSuggestSearch(
          getSuggestions,
          originalAutoSuggestSearch,
          tms?.updateDataLayer as TmsUpdateDataLayer | undefined,
        )}
        initialValues={
          {
            ...initialValues,
            destination: regionName,
            placesId,
          } as SearchFormProps['initialValues']
        }
        isSummary={true}
        isSummaryFloating={!stuck}
        translations={searchFormTranslations}
        {...(driveTimeSearchEnabled
          ? {
              onNearbyPropertiesClick: () =>
                handleNearbyPropertiesClick({
                  warningLogger: (message) => {
                    globalThis.newrelic?.noticeError(message)
                  },
                  sendOptimizelyEvent: (eventKey) => {
                    trackClientSideOptimizelyEvent(
                      eventKey,
                      optimizelyDecisionsContext,
                    )
                  },
                }),
              onNearbyPropertiesSeen: handleNearbyPropertiesSeen,
            }
          : {})}
        // I removed the more specific type (SearchFormSubmitData) because it causes a type mismatch
        // To make this more specific without the error, move the more specific type
        // into the design system
        onSubmit={(formData) => {
          delete formData.featureId
          const {
            destinationId,
            id,
            placesId,
            destination,
            radiusCentre: formRadiusCentre,
          } = formData

          const shouldSetCoordinateParams = !destinationId && ne && sw
          const catalogue = searchParams.get('catalogue')
          const radiusCentreDestination = destination?.length
            ? resolveRadiusCentre(
                radiusCentre,
                id,
                placesId,
                destinationId,
                searchParams.get('radiusCentre'),
              )
            : null

          const formDataWithDestinationURL = {
            ...formData,
            ...(formRadiusCentre && {
              displayMode: 'LIST',
            }),
            ...(catalogue && { catalogue }),
            placesId: resolvePlacesId(id, placesId, destination),
            radiusCentre: formRadiusCentre || radiusCentreDestination,
            ...(formRadiusCentre && {
              radiusKm: GD_482_RADIUS_KM,
            }),
            ...(destinationId &&
              formData.accommodationType === 'boat-holidays' && {
                regionId: destinationId,
              }),
            ...(shouldSetCoordinateParams && {
              ne,
              sw,
            }),
            // This can be removed once the upstream types for the event handler
            // have been fixed
          } as unknown as SearchFormSubmitData

          if (formRadiusCentre) {
            trackClientSideOptimizelyEvent(
              OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_SEARCH_FORM_SUBMITTED,
              optimizelyDecisionsContext,
            )
          }

          trackClientSideOptimizelyEvent(
            OPTIMIZELY_EVENTS.SEARCH_FORM_SUBMITTED,
            optimizelyDecisionsContext,
          )

          handleSearchFormSubmit(
            brand,
            formDataWithDestinationURL,
            tms,
            placesConfig,
          )
        }}
        onSearchFormSummaryClick={() => handleSearchFormSummaryClick(tms)}
        useLegacyDestinationSelector={useLegacyDestinationSelector}
        legacyListWithPlaces={{
          enabled: useLegacyDestinationSelector,
          data: placesLegacyListData,
        }}
        onAutocompleteSelected={(destinationId, source) =>
          // This is fine for now as 'undefined' passed into the includes just returns false,
          // which is proably what we want. Probably need to modify the upstream types
          ['google-textsearch', 'google-autocomplete'].includes(
            source as string,
          )
            ? setGoogleID(destinationId)
            : null
        }
        locale={locale}
        onDestinationSelectionEvent={handleDestinationSelection}
        onDateSelectionEvent={handleDateSelection}
        onGuestSelectionEvent={handleGuestSelection}
        onSearchSelectionStart={handleSearchSelectionStart}
        onSearchSelectionClose={handleSearchSelectionClose}
      />
    </div>
  )

  const { displayMode, setDisplayMode } = useDisplayContext()
  const setDisplayModeWithTms = setDisplayModeWrapper(tms, setDisplayMode)
  const searchControls = () => {
    const sortOptionsFromConfig = getSortOptionsFromConfig(
      accommodationType,
      brand,
    )

    const displaySortOptions = shouldDisplaySortOptions({
      displayMode,
    })

    const sortOptions = sortOptionsFromConfig.map((option) => ({
      text: formatMessage({
        id: `search-controls.sort.option-names.${option}`,
      }),
      value: option,
    }))

    return (
      <SearchControls
        filtersCount={totalSelectedFilters?.toString() || '0'}
        sorting={
          displaySortOptions
            ? {
                sortOptions,
                labels: {
                  selectOption: formatMessage({
                    id: 'search-controls.sort.select-option',
                    defaultMessage: 'Select sort option',
                  }),
                  selectedOptionPrefix: formatMessage({
                    id: 'search-controls.sort.selected-option-prefix',
                    defaultMessage: 'Sort',
                  }),
                },
                selectedOption: sort ?? '',
                onOptionChange: (sort: string) =>
                  dispatch({ type: SORT_ORDER_UPDATED, sort }),
              }
            : undefined
        }
        showFilters={openFilters}
        justify={['space-evenly', 'space-evenly', 'space-evenly']}
        shouldHideSortDisplayText={true}
        displayMode={displayMode}
        setDisplayMode={setDisplayModeWithTms}
        translations={{
          filterTitle: formatMessage({
            id: 'search-controls.filter.title',
            defaultMessage: 'Filters',
          }),
          filterAriaText: formatMessage({
            id: 'search-controls.filter.aria',
            defaultMessage: 'Open filters',
          }),
          listTitle: formatMessage({
            id: 'search-controls.list.title',
            defaultMessage: 'Back to list view',
          }),
          listAriaText: formatMessage({
            id: 'search-controls.list.aria',
            defaultMessage: 'Back to list view',
          }),
          mapTitle: formatMessage({
            id: 'search-controls.map.title',
            defaultMessage: 'Map',
          }),
          mapAriaText: formatMessage({
            id: 'search-controls.map.aria',
            defaultMessage: 'Map View',
          }),
        }}
      />
    )
  }

  if (mobileStickyMode) {
    return (
      <MobileSticky isGuestApp={isGuestApp}>
        {({ stuck }) => (
          <div
            /*  GD-369 Hack - adding correct gradient for James Villas */
            id={
              brand === 'jamesVillas'
                ? 'james-villas-shadow-hack-wrapper-GD-369'
                : undefined
            }
          >
            <StickableHeaderExtended stuck={stuck} hasSeparator>
              <Container>{searchForm(stuck)}</Container>
            </StickableHeaderExtended>
            {hasResults && (
              <MobileStickableContent stuck={stuck}>
                {searchControls()}
              </MobileStickableContent>
            )}
          </div>
        )}
      </MobileSticky>
    )
  }

  return (
    <div>
      <Container>{searchForm(true)}</Container>
      {(hasResults || displayMode === 'MAP') && <>{searchControls()}</>}
    </div>
  )
}
